import Cookies from 'js-cookie';

import { ANNUAL_VALUES_FOR_MEMBERSHIPS, MEMBERSHIPS } from '@lib/constants';
import type { ConsentPreferences } from '@lib/consent';
import {
  getFacebookBrowserId,
  getFacebookClickId,
  trackingPurchasePixelIDKey
} from '@lib/fpixel';

/**
 * FFH-1170 — `purchase` dataLayer push for the confirmation page (`/thank-you`).
 *
 * This is the browser/pixel side of the dedup pair documented in the GTM
 * unified architecture brief (§1.2 DLBase, §1.3 DLPurchase, §2 event id). The
 * Meta / Google Ads / TikTok / Snap GTM tags all read from this single push.
 *
 * IMPORTANT — event id contract. The brief proposes a deterministic
 * `ffh_purch_<stripeSessionId>` (Pattern B), but the *deployed* backend
 * (FFH-967 / FFH-603) does NOT derive that. The live Stripe-webhook Purchase
 * CAPI fanout dedups on `purchase_event_id` read from `subscription.metadata`,
 * which the checkout handler populates with the browser's `purchasePixelEventId`
 * (a random id minted on the checkout page and stored in
 * `localStorage[trackingPurchasePixelIDKey]`). To dedup with what is actually
 * shipped — and with the legacy FB / TikTok pixels already on this page — the
 * dataLayer push must carry that same id. `resolvePurchaseEventId` reads it from
 * localStorage and only falls back to the deterministic id when it is absent
 * (e.g. a cross-device confirmation where the checkout localStorage is gone).
 */

export type ConsentSignal = 'granted' | 'denied';

export interface DLConsent {
  ad_user_data: ConsentSignal;
  ad_personalization: ConsentSignal;
  ad_storage: ConsentSignal;
  analytics_storage: ConsentSignal;
}

export type PlanTier = 'starter' | 'pro' | 'ultra';

export type LeadFunnel = 'team_rating' | 'mini_league_readiness';

export interface DLUser {
  /** Plaintext email. undefined when the confirmation is unauthenticated. */
  email?: string;
  /** Plaintext E.164 phone. No source on the confirmation page today. */
  phoneE164?: string;
  /**
   * Canonical user id = Auth0 `sub`. The backend hashes the same sub into
   * every CAPI platform's external_id, so passing the sub here lets the pixel
   * libraries produce a matching hash. Optional because a logged-out
   * confirmation has no sub.
   */
  externalId?: string;
}

export interface DLClick {
  fbc?: string;
  fbp?: string;
  ttclid?: string;
  ttp?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  sc_click_id?: string;
}

export interface DLPage {
  source_url: string;
  referrer?: string;
}

export interface DLPurchase {
  event: 'purchase';
  event_id: string;
  value: number;
  // FFH-1847: ISO 4217, no longer GBP-only — localised purchases report the
  // charged currency so this rail matches the server webhook event.
  currency: string;
  plan_tier?: PlanTier;
  stripe_session_id: string;
  user: DLUser;
  click: DLClick;
  page: DLPage;
  consent: DLConsent;
}

/**
 * FFH-990 — `lead` dataLayer event (architecture §1.4). Fired at the team-rating
 * result reveal and the mini-league-readiness verdict reveal. `event_id` is
 * Pattern A (a fresh UUID minted at the push, shared with the CAPI shim).
 * `value` / `currency` are optional and unused today (no lead valuation).
 */
export interface DLLead {
  event: 'lead';
  event_id: string;
  funnel: LeadFunnel;
  funnel_result?: string;
  value?: number;
  currency?: string;
  user: DLUser;
  click: DLClick;
  page: DLPage;
  consent: DLConsent;
}

export const PURCHASE_CURRENCY = 'GBP' as const;

/**
 * Consent Mode v2 defaults to `denied` before the user acts (cross-cutting
 * rule 5), so an absent CookieYes decision maps to denied across the board.
 */
const DENIED_CONSENT: DLConsent = {
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  ad_storage: 'denied',
  analytics_storage: 'denied'
};

/**
 * The sellable starter / pro / ultra plan ids (monthly + yearly). Built from
 * `MEMBERSHIPS` so it tracks the catalogue without dragging the heavy
 * `utils.tsx` module into this lib. Legacy premium / standard / free ids are
 * intentionally absent — they are not part of the DLPurchase `plan_tier` enum,
 * so those checkouts push without a `plan_tier` (matching the backend, which
 * drops non-{starter,pro,ultra} tiers from the Purchase fanout).
 */
const PLAN_ID_TO_TIER: Record<number, PlanTier> = {
  [MEMBERSHIPS.STARTER_MONTHLY_ID]: 'starter',
  [MEMBERSHIPS.STARTER_YEARLY_ID]: 'starter',
  [MEMBERSHIPS.PRO_MONTHLY_ID]: 'pro',
  [MEMBERSHIPS.PRO_YEARLY_ID]: 'pro',
  [MEMBERSHIPS.ULTRA_MONTHLY_ID]: 'ultra',
  [MEMBERSHIPS.ULTRA_YEARLY_ID]: 'ultra',
  // FFH-1744: the alt-pricing arm is not a new tier — it is the same Starter
  // and Pro at a different price, and must report as such.
  [MEMBERSHIPS.STARTER_YEARLY_ALT_ID]: 'starter',
  [MEMBERSHIPS.PRO_YEARLY_ALT_ID]: 'pro'
};

/** Click-ID cookies the FE-fallback writer (FFH-1160) sets non-HttpOnly. */
const CLICK_COOKIE_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid',
  'sc_click_id'
] as const;

/**
 * Deterministic event id derived from the Stripe Checkout session id. Used only
 * as a fallback when the checkout-minted id is unavailable (see module note).
 */
export const deriveFfhPurchaseEventId = (
  stripeCheckoutSessionId: string
): string => `ffh_purch_${stripeCheckoutSessionId}`;

/**
 * The dedup id the push must carry: the checkout-minted `purchasePixelEventId`
 * (also written to Stripe metadata as `purchase_event_id` and consumed by the
 * live CAPI). Falls back to the deterministic id only when localStorage has no
 * value to offer.
 */
export const resolvePurchaseEventId = (stripeSessionId: string): string => {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(trackingPurchasePixelIDKey);
      if (stored && stored.length > 0) return stored;
    } catch {
      // localStorage can throw in private mode, when storage is disabled, or
      // inside some in-app webviews. Fall through to the deterministic id
      // rather than letting the dedup contract break.
    }
  }
  return deriveFfhPurchaseEventId(stripeSessionId);
};

/**
 * Pattern A event id (architecture §2.1): a fresh UUID minted at the moment of
 * the dataLayer push. The SAME id must be passed to both the dataLayer push
 * (read by the GTM pixel) and the CAPI shim, so the two rails dedup on one
 * value.
 */
export const mintEventId = (): string => {
  const webCrypto = typeof crypto === 'undefined' ? undefined : crypto;
  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    // Fallback for browsers without crypto.randomUUID (older Safari): an
    // RFC 4122 v4 built from crypto.getRandomValues — strong entropy matters
    // for a dedup key, and getRandomValues exists wherever randomUUID does
    // not. Version/variant bits are set with modulo arithmetic (no bitwise).
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) % 16) + 0x40; // version 4 (high nibble)
    bytes[8] = ((bytes[8] ?? 0) % 64) + 0x80; // variant 10xx (high bits)
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join('')
    ].join('-');
  }

  // Last resort with no Web Crypto at all (never in a browser): Math.random.
  const hex = (length: number): string =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${hex(4)}-${hex(12)}`;
};

/**
 * Collapse CookieYes preferences to the four Google consent signals. The
 * `advertisement` category drives the three ad signals; `analytics` drives
 * `analytics_storage`. A missing decision (`null`) is denied across the board.
 */
export const mapConsentToSignals = (
  consent: ConsentPreferences | null
): DLConsent => {
  if (!consent) return { ...DENIED_CONSENT };
  const advertisement: ConsentSignal = consent.advertisement
    ? 'granted'
    : 'denied';
  return {
    ad_user_data: advertisement,
    ad_personalization: advertisement,
    ad_storage: advertisement,
    analytics_storage: consent.analytics ? 'granted' : 'denied'
  };
};

/** Maps a plan id to a DLPurchase tier, or undefined for non-sellable tiers. */
export const resolvePlanTier = (
  planId: string | number | undefined
): PlanTier | undefined => {
  const numeric = Number(planId);
  if (Number.isNaN(numeric)) return undefined;
  return PLAN_ID_TO_TIER[numeric];
};

/**
 * Purchase value for the plan. Mirrors the sibling FB / TikTok pixels on this
 * page (`ANNUAL_VALUES_FOR_MEMBERSHIPS`); unmapped plans report 0. The Stripe
 * `unit_amount` remains the authoritative server-side value on the CAPI rail.
 */
export const resolvePurchaseValue = (
  planId: string | number | undefined
): number => ANNUAL_VALUES_FOR_MEMBERSHIPS?.[Number(planId)] || 0;

/**
 * Best-effort read of click identifiers available client-side at confirmation.
 * `_fbp` (FB SDK) is reliably readable; `_fbc` is readable only when not
 * HttpOnly; the worker writes the rest HttpOnly, so they appear only on the
 * direct-hit FE-fallback path. Every field is optional — the CAPI rail already
 * has the worker-captured ids from Stripe metadata, and dedup keys on
 * `event_id`, not click ids.
 */
export const readClickIds = (): DLClick => {
  const click: DLClick = {};
  if (typeof window === 'undefined') return click;

  const fbc = getFacebookClickId();
  if (fbc) click.fbc = fbc;

  const fbp = getFacebookBrowserId(document);
  if (fbp) click.fbp = fbp;

  for (const key of CLICK_COOKIE_KEYS) {
    const value = Cookies.get(key);
    if (value) click[key] = value;
  }

  return click;
};

export interface BuildPurchaseEventInput {
  eventId: string;
  stripeSessionId: string;
  planId: string | number | undefined;
  /**
   * FFH-1847: the actual charged amount + currency from /billing/confirm.
   * Null (e.g. a backend that hasn't sent them, or a session with no total)
   * falls back to the GBP plan dict + PURCHASE_CURRENCY.
   */
  amount?: number | null;
  currency?: string | null;
  email?: string;
  externalId?: string;
  click: DLClick;
  sourceUrl: string;
  referrer?: string;
  consent: DLConsent;
}

/** Pure assembler for the DLPurchase payload; drops undefined optionals. */
export const buildPurchaseEvent = (
  input: BuildPurchaseEventInput
): DLPurchase => {
  const planTier = resolvePlanTier(input.planId);

  const user: DLUser = {};
  if (input.email) user.email = input.email;
  if (input.externalId) user.externalId = input.externalId;

  const page: DLPage = { source_url: input.sourceUrl };
  if (input.referrer) page.referrer = input.referrer;

  // FFH-1847: the charged amount is only meaningful with its currency, so use
  // the pair only when BOTH are present — a lone amount (or blank currency)
  // would report the charged number under GBP, recreating the very mismatch
  // this fixes. `!= null` keeps a genuine £0 (100%-coupon) charge.
  const chargedCurrency = input.currency?.trim().toUpperCase();
  const { value, currency } =
    input.amount != null && chargedCurrency
      ? { value: input.amount, currency: chargedCurrency }
      : {
          value: resolvePurchaseValue(input.planId),
          currency: PURCHASE_CURRENCY
        };

  return {
    event: 'purchase',
    event_id: input.eventId,
    value,
    currency,
    ...(planTier && { plan_tier: planTier }),
    stripe_session_id: input.stripeSessionId,
    user,
    click: input.click,
    page,
    consent: input.consent
  };
};

/** Pushes the assembled event onto `window.dataLayer` (SSR-safe). */
export const pushPurchase = (payload: DLPurchase): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

/**
 * `start_trial` — fired on the confirmation page for trial checkouts instead
 * of `purchase` (Matt, 2026-07-09: the browser must not count a purchase at
 * trial start; the Purchase for a trial comes from the server-side CAPI when
 * Stripe flips it trialing→active). Pixel-only — no CAPI counterpart — read
 * by the GTM Start Trial tags (Meta StartTrial / Google Ads FFH Start Trial)
 * on the `start_trial` custom-event trigger.
 */
export interface DLStartTrial {
  event: 'start_trial';
  event_id: string;
  value: number;
  // FFH-1847: trial start is always the GBP annual estimate (nothing is
  // charged yet), so this stays the 'GBP' literal — a compile-time guard
  // against a non-GBP currency ever reaching a start_trial event.
  currency: 'GBP';
  plan_tier?: PlanTier;
  stripe_session_id: string;
  user: DLUser;
  click: DLClick;
  page: DLPage;
  consent: DLConsent;
}

/**
 * Pure assembler for the DLStartTrial payload. Takes the same input as
 * `buildPurchaseEvent`; `eventId` is the session's purchase event id, from
 * which the start_trial id is derived (`_trial` suffix) — deterministic per
 * session but distinct from the Purchase id the trial→paid CAPI event will
 * carry, so a pixel-only event never reuses an id that has a CAPI counterpart.
 */
export const buildStartTrialEvent = (
  input: BuildPurchaseEventInput
): DLStartTrial => {
  // FFH-1847: the charged amount/currency are a Purchase-only concept (nothing
  // is charged at trial start). Force them absent so start_trial always
  // reports the GBP annual estimate regardless of what a caller passes — the
  // contract lives in the builder, not the caller's discipline.
  return {
    ...buildPurchaseEvent({ ...input, amount: undefined, currency: undefined }),
    event: 'start_trial',
    event_id: `${input.eventId}_trial`,
    // Pin to the 'GBP' literal: the spread carries DLPurchase's widened
    // `string` currency, and forcing it here keeps the DLStartTrial contract
    // (and stays correct if buildPurchaseEvent ever changes).
    currency: PURCHASE_CURRENCY
  };
};

/** Pushes the assembled event onto `window.dataLayer` (SSR-safe). */
export const pushStartTrial = (payload: DLStartTrial): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

export interface BuildLeadEventInput {
  eventId: string;
  funnel: LeadFunnel;
  funnelResult?: string;
  value?: number;
  currency?: string;
  email?: string;
  externalId?: string;
  click: DLClick;
  sourceUrl: string;
  referrer?: string;
  consent: DLConsent;
}

/** Pure assembler for the DLLead payload; drops undefined optionals. */
export const buildLeadEvent = (input: BuildLeadEventInput): DLLead => {
  const user: DLUser = {};
  if (input.email) user.email = input.email;
  if (input.externalId) user.externalId = input.externalId;

  const page: DLPage = { source_url: input.sourceUrl };
  if (input.referrer) page.referrer = input.referrer;

  return {
    event: 'lead',
    event_id: input.eventId,
    funnel: input.funnel,
    ...(input.funnelResult !== undefined && {
      funnel_result: input.funnelResult
    }),
    ...(input.value !== undefined && { value: input.value }),
    ...(input.currency && { currency: input.currency }),
    user,
    click: input.click,
    page,
    consent: input.consent
  };
};

/** Pushes the assembled lead event onto `window.dataLayer` (SSR-safe). */
export const pushLead = (payload: DLLead): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

/**
 * FFH-606 — `view_content` dataLayer event (Pattern A). Fired on the funnel
 * landing-page load; a fresh UUID is minted at the push and shared with the
 * CAPI shim. Upper-funnel, so no value/plan_tier.
 */
export interface DLViewContent {
  event: 'view_content';
  event_id: string;
  user: DLUser;
  click: DLClick;
  page: DLPage;
  consent: DLConsent;
}

/**
 * FFH-991 — `add_to_cart` dataLayer event (Pattern A). Fired when the checkout
 * page is reached / a Stripe Checkout session is initiated. `value` /
 * `plan_tier` are included when the plan is known at this point.
 */
export interface DLAddToCart {
  event: 'add_to_cart';
  event_id: string;
  value?: number;
  currency?: 'GBP';
  plan_tier?: PlanTier;
  user: DLUser;
  click: DLClick;
  page: DLPage;
  consent: DLConsent;
}

export interface BuildViewContentEventInput {
  eventId: string;
  email?: string;
  externalId?: string;
  click: DLClick;
  sourceUrl: string;
  referrer?: string;
  consent: DLConsent;
}

/** Pure assembler for the DLViewContent payload; drops undefined optionals. */
export const buildViewContentEvent = (
  input: BuildViewContentEventInput
): DLViewContent => {
  const user: DLUser = {};
  if (input.email) user.email = input.email;
  if (input.externalId) user.externalId = input.externalId;

  const page: DLPage = { source_url: input.sourceUrl };
  if (input.referrer) page.referrer = input.referrer;

  return {
    event: 'view_content',
    event_id: input.eventId,
    user,
    click: input.click,
    page,
    consent: input.consent
  };
};

/** Pushes the assembled view-content event onto `window.dataLayer` (SSR-safe). */
export const pushViewContent = (payload: DLViewContent): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

export interface BuildAddToCartEventInput {
  eventId: string;
  planId?: string | number;
  email?: string;
  externalId?: string;
  click: DLClick;
  sourceUrl: string;
  referrer?: string;
  consent: DLConsent;
}

/** Pure assembler for the DLAddToCart payload; drops undefined optionals. */
export const buildAddToCartEvent = (
  input: BuildAddToCartEventInput
): DLAddToCart => {
  const planTier = resolvePlanTier(input.planId);
  const value = resolvePurchaseValue(input.planId);

  const user: DLUser = {};
  if (input.email) user.email = input.email;
  if (input.externalId) user.externalId = input.externalId;

  const page: DLPage = { source_url: input.sourceUrl };
  if (input.referrer) page.referrer = input.referrer;

  return {
    event: 'add_to_cart',
    event_id: input.eventId,
    ...(value ? { value, currency: PURCHASE_CURRENCY } : {}),
    ...(planTier && { plan_tier: planTier }),
    user,
    click: input.click,
    page,
    consent: input.consent
  };
};

/** Pushes the assembled add-to-cart event onto `window.dataLayer` (SSR-safe). */
export const pushAddToCart = (payload: DLAddToCart): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};
